import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateSettings } from "store/Actions/settings";
import {
    Tokens,
    resolveThemeMode,
    resolveEffectiveTheme,
    buildThemeSetting,
    getSystemPrefersDark,
    applyThemeToDocument,
} from "Shared/Tokens";

const THEME_OPTIONS = [
    { key: "light", label: "Light" },
    { key: "dark", label: "Dark" },
    { key: "auto", label: "Auto" },
];

export const ThemeProvider = ({ children }) => {
    const theme = useSelector((state) => state.settings?.theme);
    const [systemDark, setSystemDark] = useState(() => getSystemPrefersDark());

    useEffect(() => {
        if (
            typeof window === "undefined" ||
            typeof window.matchMedia !== "function"
        ) {
            return undefined;
        }
        const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
        const handleChange = (event) => {
            setSystemDark(Boolean(event.matches));
        };
        if (typeof mediaQuery.addEventListener === "function") {
            mediaQuery.addEventListener("change", handleChange);
            return () => mediaQuery.removeEventListener("change", handleChange);
        }
        if (typeof mediaQuery.addListener === "function") {
            mediaQuery.addListener(handleChange);
            return () => mediaQuery.removeListener(handleChange);
        }
        return undefined;
    }, []);

    const effectiveTheme = resolveEffectiveTheme(theme, systemDark);

    useEffect(() => {
        applyThemeToDocument(effectiveTheme);
    }, [effectiveTheme]);

    return (
        <div
            className={effectiveTheme === "dark" ? "theme-dark" : "theme-light"}
            data-theme={effectiveTheme}
            style={{
                minHeight: "100vh",
                backgroundColor: Tokens.bg,
                color: Tokens.textPrimary,
            }}
        >
            {children}
        </div>
    );
};

export const ThemeSwitcher = ({ testIdPrefix = "settings" }) => {
    const dispatch = useDispatch();
    const theme = useSelector((state) => state.settings?.theme);
    const activeMode = resolveThemeMode(theme);

    const handleSelectMode = (mode) => {
        const nextTheme = buildThemeSetting(mode);
        applyThemeToDocument(resolveEffectiveTheme(nextTheme));
        dispatch(updateSettings({ theme: nextTheme }));
    };

    return (
        <div
            data-testid={`${testIdPrefix}-theme-switcher`}
            role="radiogroup"
            aria-label="Theme mode"
            style={{
                display: "inline-flex",
                alignItems: "center",
                backgroundColor: Tokens.raised,
                border: `1px solid ${Tokens.hairlineStrong}`,
                borderRadius: Tokens.radiusControl,
                padding: "2px",
                gap: "2px",
                flexShrink: 0,
            }}
        >
            {THEME_OPTIONS.map((opt) => {
                const isActive = activeMode === opt.key;
                return (
                    <button
                        key={opt.key}
                        type="button"
                        role="radio"
                        aria-checked={isActive}
                        data-testid={`${testIdPrefix}-theme-option-${opt.key}`}
                        onClick={() => handleSelectMode(opt.key)}
                        style={{
                            border: isActive
                                ? `1px solid ${Tokens.accentBorder}`
                                : "1px solid transparent",
                            backgroundColor: isActive
                                ? Tokens.card
                                : "transparent",
                            color: isActive
                                ? Tokens.textPrimary
                                : Tokens.textSecondary,
                            fontWeight: isActive ? 600 : 500,
                            fontSize: "12px",
                            padding: "4px 10px",
                            borderRadius: "6px",
                            cursor: "pointer",
                            lineHeight: 1.3,
                            transition:
                                "background-color 150ms ease, color 150ms ease, border-color 150ms ease",
                        }}
                    >
                        {opt.label}
                    </button>
                );
            })}
        </div>
    );
};

export default ThemeSwitcher;
